'use client';

import React, { useState } from 'react';
import { Copy, CheckCheck, Zap } from 'lucide-react';
import { toast } from 'sonner';

interface Props {
  role: 'player' | 'admin';
}

const CREDENTIALS = {
  player: { email: 'striker99@fishblast.gg', password: 'Blast#2026', label: 'Player Demo' },
  admin: { email: 'operator@fishblast.gg', password: 'Admin#2026', label: 'Admin Demo' },
};

export default function DemoCredentials({ role }: Props) {
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const creds = CREDENTIALS[role];

  const copyToClipboard = async (value: string, field: string) => {
    await navigator.clipboard.writeText(value);
    setCopiedField(field);
    toast.success(`${field} copied to clipboard`);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const autofill = () => {
    // Autofill login form fields via exposed setValue
    const setVal = (window as unknown as Record<string, unknown>).__loginSetValue as ((field: string, value: string) => void) | undefined;
    if (setVal) {
      setVal('email', creds.email);
      setVal('password', creds.password);
      toast.success('Demo credentials filled in!');
    }
  };

  return (
    <div className="mt-6 rounded-xl p-4 animate-fade-in"
      style={{ background: 'rgba(6,182,212,0.06)', border: '1px solid rgba(6,182,212,0.2)' }}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Zap size={14} className="neon-text-cyan" />
          <span className="text-xs font-semibold uppercase tracking-widest" style={{ color: 'var(--primary)' }}>
            {creds.label} — Quick Access
          </span>
        </div>
        <button
          onClick={autofill}
          className="text-xs px-3 py-1 rounded-md font-semibold transition-all duration-150 hover:opacity-80 active:scale-95"
          style={{ background: 'rgba(6,182,212,0.2)', color: 'var(--primary)' }}
        >
          Autofill
        </button>
      </div>

      <div className="flex flex-col gap-2">
        {[
          { label: 'Email', value: creds.email, field: 'Email' },
          { label: 'Password', value: creds.password, field: 'Password' },
        ].map((item) => (
          <div key={`cred-${item.field}`} className="flex items-center justify-between gap-2">
            <span className="text-xs text-muted-foreground w-16">{item.label}</span>
            <code className="flex-1 text-xs font-mono truncate" style={{ color: 'var(--foreground)' }}>
              {item.field === 'Password' ? '••••••••••' : item.value}
            </code>
            <button
              onClick={() => copyToClipboard(item.value, item.field)}
              className="flex-shrink-0 p-1 rounded transition-all duration-150 hover:opacity-80 active:scale-95"
              style={{ color: 'var(--primary)' }}
            >
              {copiedField === item.field ? <CheckCheck size={13} /> : <Copy size={13} />}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}